import { Box, Button, Flex, Text, VStack } from '@chakra-ui/react'
import { SolidActionButton } from '@components/Buttons'
import { GenericEditModal } from '@components/DataView/GenericEditModal'
import {
  DOT_PHRASE_TEMPLATES,
  DotPhraseTemplate,
  FieldMap,
  FieldMapValue,
  FieldTypes,
  MIDWIVES,
  WithId,
} from '@hb/shared'
import { addDoc, collection, CollectionReference, deleteDoc, doc } from 'firebase/firestore'
import { useCallback, useMemo, useRef, useState } from 'react'
import { db } from '../../../backend/db/db'
import { useQuery } from '../../../hooks/backend/useQuery'

const practiceTemplateField: FieldMap = {
  name: 'Practice Template',
  children: {
    keyword: {
      type: FieldTypes.TEXT,
      placeholder: 'Keyword (e.g. hpi, ros, plan) — providers type .keyword + Tab',
    },
    body: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'Template text — use [placeholder] for parts to fill in',
    },
    description: {
      type: FieldTypes.TEXT,
      placeholder: 'Short description shown in the picker (optional)',
      optional: true,
    },
  },
}

const EMPTY_DATA: FieldMapValue = {}

const TemplateRow = ({
  template,
  onDelete,
  deleting,
}: {
  template: WithId<DotPhraseTemplate>
  onDelete: (t: WithId<DotPhraseTemplate>) => void
  deleting: boolean
}) => (
  <Flex
    align="center"
    gap={2}
    px={3}
    py={2}
    borderBottomWidth="1px"
    borderColor="gray.100"
    _dark={{ borderColor: 'gray.700' }}>
    <Box flex={1} minW={0}>
      <Text fontSize="sm" fontWeight={600}>
        .{template.keyword}
      </Text>
      <Text fontSize="xs" color="gray.500" noOfLines={1}>
        {template.description || template.body}
      </Text>
    </Box>
    <Button
      size="xs"
      colorScheme="red"
      variant="ghost"
      isLoading={deleting}
      onClick={() => onDelete(template)}>
      Remove
    </Button>
  </Flex>
)

export const PracticeTemplateList = ({ practiceId }: { practiceId: string }) => {
  const practiceRef = useMemo(
    () =>
      collection(
        db,
        MIDWIVES,
        practiceId,
        DOT_PHRASE_TEMPLATES,
      ) as CollectionReference<DotPhraseTemplate>,
    [practiceId],
  )
  const { dataArr: templates } = useQuery<DotPhraseTemplate>(practiceRef)
  const [addOpen, setAddOpen] = useState(false)
  const deletingRef = useRef<Set<string>>(new Set())
  const [, forceRender] = useState(0)

  const handleAdd = useCallback(
    async (values: FieldMapValue) => {
      await addDoc(collection(db, MIDWIVES, practiceId, DOT_PHRASE_TEMPLATES), {
        keyword: (values.keyword as string).trim().toLowerCase(),
        body: values.body as string,
        description: (values.description as string | undefined) ?? '',
        level: 'practice',
        createdOn: Date.now(),
        updatedOn: Date.now(),
      } satisfies DotPhraseTemplate).catch(console.error)
      setAddOpen(false)
      return {}
    },
    [practiceId],
  )

  const handleDelete = useCallback(
    async (t: WithId<DotPhraseTemplate>) => {
      deletingRef.current.add(t.id)
      forceRender(n => n + 1)
      await deleteDoc(doc(db, MIDWIVES, practiceId, DOT_PHRASE_TEMPLATES, t.id)).catch(
        console.error,
      )
      deletingRef.current.delete(t.id)
      forceRender(n => n + 1)
    },
    [practiceId],
  )

  return (
    <>
      <VStack align="stretch" spacing={0}>
        {!templates || templates.length === 0 ? (
          <Text fontSize="sm" color="gray.500" p={3}>
            No practice templates yet. Click "Add Template" to create a shared template visible to
            all providers in this practice.
          </Text>
        ) : (
          templates.map(t => (
            <TemplateRow
              key={t.id}
              template={t}
              onDelete={handleDelete}
              deleting={deletingRef.current.has(t.id)}
            />
          ))
        )}
        <Flex px={3} py={2}>
          <SolidActionButton size="sm" onClick={() => setAddOpen(true)}>
            Add Template
          </SolidActionButton>
        </Flex>
      </VStack>
      <GenericEditModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        field={practiceTemplateField}
        data={EMPTY_DATA}
        onSubmit={handleAdd}
      />
    </>
  )
}
